import { useState, useEffect, useCallback } from "react";
import { apiCall } from "../api";
import { refreshMaintenanceTypes } from "../maintenanceTypesCache";

// Settings -> Maintenance Types.
//
// Four types are built into the product and shown here read-only. Breakdown in
// particular is what MTBF, MTTR and downtime are counted from, and the other three are
// what the free-text classifier resolves to, so a settings screen must not be able to
// rename or remove them.
//
// Everything else is added here. The one thing a new type has to declare is whether it
// counts as an unplanned breakdown, because that is the only question the reliability
// report asks of a type.
export default function MaintenanceTypesAdmin() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newIsBreakdown, setNewIsBreakdown] = useState(false);
  const [editing, setEditing] = useState(null);      // { id, label, isBreakdown }

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const rows = await apiCall("GET", "/maintenance-types");
      setTypes(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load maintenance types");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function afterChange() {
    await load();
    await refreshMaintenanceTypes();   // the log form's dropdown reads this
  }

  async function create() {
    if (!newLabel.trim()) return;
    setSaving(true); setErr("");
    try {
      await apiCall("POST", "/maintenance-types", { label: newLabel.trim(), isBreakdown: newIsBreakdown });
      setNewLabel(""); setNewIsBreakdown(false);
      await afterChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not add that type");
    } finally { setSaving(false); }
  }

  async function saveEdit() {
    if (!editing) return;
    setSaving(true); setErr("");
    try {
      await apiCall("PUT", `/maintenance-types/${editing.id}`,
        { label: editing.label.trim(), isBreakdown: editing.isBreakdown });
      setEditing(null);
      await afterChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save that change");
    } finally { setSaving(false); }
  }

  async function remove(t) {
    if (!window.confirm(`Remove "${t.label}"? Entries already recorded as this type are not changed.`)) return;
    setErr("");
    try {
      await apiCall("DELETE", `/maintenance-types/${t.id}`);
      await afterChange();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not remove that type");
    }
  }

  return (
    <div className="space-y-4" data-testid="maintenance-types-admin">
      <div>
        <h3 className="font-semibold text-gray-800 text-sm">Maintenance Types</h3>
        <p className="text-xs text-gray-500">
          The choices in the Maintenance Type box on a maintenance log. The four built-in
          types cannot be changed — Breakdown is what MTBF, MTTR and downtime are counted from.
        </p>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3" data-testid="mt-error">{err}</div>}

      <div className="bg-white rounded-xl border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b">
              {["Type", "Identifier", "Counts as breakdown", ""].map(h => (
                <th key={h} className="text-left px-4 py-2 text-xs text-gray-500 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-6 text-sm text-gray-500">Loading…</td></tr>
            ) : types.map(t => (
              <tr key={t.id} className="border-b last:border-0 hover:bg-gray-50" data-testid={`mt-row-${t.key}`}>
                <td className="px-4 py-3">
                  {editing?.id === t.id ? (
                    <input value={editing.label} onChange={e => setEditing(x => ({ ...x, label: e.target.value }))}
                      className="border border-gray-300 rounded-lg px-2 py-1 text-sm w-full"
                      data-testid="mt-edit-label" />
                  ) : (
                    <span className="font-medium text-gray-800">{t.label}</span>
                  )}
                  {t.system && <span className="ml-2 text-[10px] font-bold text-gray-400 uppercase">Built in</span>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{t.key}</td>
                <td className="px-4 py-3">
                  {editing?.id === t.id ? (
                    <label className="inline-flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" checked={editing.isBreakdown}
                        onChange={e => setEditing(x => ({ ...x, isBreakdown: e.target.checked }))}
                        data-testid="mt-edit-breakdown" />
                      Counts as an unplanned breakdown
                    </label>
                  ) : (
                    <span className={`text-xs font-semibold ${t.isBreakdown ? "text-red-600" : "text-gray-400"}`}>
                      {t.isBreakdown ? "Yes — counted in MTBF / MTTR" : "No — planned work"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {t.system ? (
                    <span className="text-xs text-gray-300">—</span>
                  ) : editing?.id === t.id ? (
                    <>
                      <button onClick={saveEdit} disabled={saving}
                        className="text-xs font-semibold text-green-600 hover:text-green-700 disabled:opacity-50"
                        data-testid="mt-save-edit">{saving ? "Saving…" : "Save"}</button>
                      <button onClick={() => setEditing(null)}
                        className="ml-3 text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditing({ id: t.id, label: t.label, isBreakdown: !!t.isBreakdown })}
                        className="text-xs text-blue-400 hover:text-blue-600"
                        data-testid={`mt-edit-${t.key}`}>Edit</button>
                      <button onClick={() => remove(t)}
                        className="ml-3 text-xs text-red-300 hover:text-red-500"
                        data-testid={`mt-delete-${t.key}`}>Del</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-gray-50 rounded-xl border p-4 space-y-3">
        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Add a type</h4>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="text-xs text-gray-500">Name</label>
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
              placeholder="e.g. Predictive Maintenance Plan"
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm mt-0.5"
              data-testid="mt-new-label" />
          </div>
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 pb-2">
            <input type="checkbox" checked={newIsBreakdown} onChange={e => setNewIsBreakdown(e.target.checked)}
              data-testid="mt-new-breakdown" />
            Counts as an unplanned breakdown
          </label>
          <button onClick={create} disabled={saving || !newLabel.trim()}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-40"
            data-testid="mt-add-btn">{saving ? "Adding…" : "Add Type"}</button>
        </div>
        <p className="text-xs text-gray-500">
          Leave the box unticked for planned work — it will be kept out of the breakdown
          count, downtime and MTBF / MTTR, the same as Scheduled PM.
        </p>
      </div>
    </div>
  );
}
